import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@auth/AuthContext';
import { useAppDispatch, useAppSelector } from '@hooks/useAppDispatch';
import { useProviderUser } from '@hooks/useProviderUser';
import { toggleDrawer } from '@store/slices/menuSlice';
import { Menu, X, LogOut, Home, ChevronLeft, Building2, Database, Shield, History, Users, BarChart3, Target, Bell } from 'lucide-react';
import { Footer } from '@components/Footer';
import { getNavItemsForGroups, filterNavItemsByAccess } from '@routes/navConfig';
import { useGetMyMembershipQuery } from '@api/endpoints/onboardingApi';
import { useGetMyAccessQuery } from '@api/endpoints/accessControlApi';
import AcceptInviteHandler from '@components/AcceptInviteHandler';

const ICON_MAP: Record<string, React.ElementType> = {
  Home, Building2, Database, Shield, History, Users, BarChart3, Target, Bell,
};

/**
 * Layout for protected routes: top header bar, collapsible sidebar, main content.
 *
 * Responsive behaviour:
 * - Desktop (>1024px): sidebar expanded (260px), collapsible via toggle
 * - Tablet (768-1024px): sidebar collapsed by default, expandable
 * - Mobile (<768px): sidebar hidden, hamburger overlay
 */
export function ProtectedLayout() {
  const { user, signOut } = useAuth();
  const dispatch = useAppDispatch();
  const drawerOpen = useAppSelector((s) => s.menu.drawerOpen);
  const navigate = useNavigate();
  const { isProviderUser, providerMapping } = useProviderUser();

  // Resolve the caller's practice and effective Component access so the nav is
  // policy-driven: a feature Component's nav entry shows only when its
  // componentId is accessible under the per-practice Access_Policy (Req 10.2).
  // Owners / medrender already have every componentId in accessibleComponentIds.
  const { data: membershipData } = useGetMyMembershipQuery();
  const clinicId = membershipData?.membership?.clinicId ?? '';
  const { data: myAccess } = useGetMyAccessQuery(clinicId, { skip: !clinicId });

  // For provider-users, the "home" link should go to their scorecard so they
  // don't get bounced back-and-forth by ProviderHomeRedirect.
  const homePath = isProviderUser && providerMapping
    ? `/analytics/provider-scorecard?view=completed&provider=${encodeURIComponent(providerMapping)}`
    : '/home';

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen flex-col">
      {/* Top header bar */}
      <header className="sticky top-0 z-50 flex h-14 items-center justify-between border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-3">
          {/* Hamburger / toggle */}
          <button
            onClick={() => dispatch(toggleDrawer())}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            aria-label={drawerOpen ? 'Close sidebar' : 'Open sidebar'}
          >
            {drawerOpen ? <ChevronLeft className="h-5 w-5 hidden lg:block" /> : null}
            {drawerOpen ? <X className="h-5 w-5 lg:hidden" /> : <Menu className="h-5 w-5" />}
          </button>
          <Link to={homePath} className="font-semibold text-lg text-primary">
            MedRender
          </Link>
        </div>

        {/* User menu */}
        <div className="flex items-center gap-3">
          <span className="hidden text-sm text-muted-foreground sm:inline">
            {user?.name || user?.email}
          </span>
          <button
            onClick={handleSignOut}
            className="inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Mobile overlay backdrop */}
        {drawerOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/50 lg:hidden"
            onClick={() => dispatch(toggleDrawer())}
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        <aside
          className={[
            'fixed top-14 bottom-0 z-40 flex flex-col border-r bg-background transition-all duration-200',
            // Mobile: overlay drawer
            drawerOpen ? 'left-0 w-[260px]' : '-left-[260px]',
            // Desktop: inline sidebar
            'lg:static lg:z-auto',
            drawerOpen ? 'lg:w-[260px]' : 'lg:w-0 lg:overflow-hidden lg:border-r-0',
          ].join(' ')}
        >
          <nav className="flex-1 space-y-1 p-3">
            {(isProviderUser && providerMapping
              ? [{
                  label: 'My Scorecard',
                  path: `/analytics/provider-scorecard?view=completed&provider=${encodeURIComponent(providerMapping)}`,
                  icon: 'BarChart3',
                }]
              : filterNavItemsByAccess(
                  getNavItemsForGroups(user?.groups ?? []),
                  myAccess?.accessibleComponentIds,
                )
            ).map((item) => {
              const Icon = ICON_MAP[item.icon];
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                >
                  {Icon && <Icon className="h-4 w-4" />}
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <AcceptInviteHandler />
          <Outlet />
        </main>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
